import Vue from 'vue'
import _ from 'lodash'
import dayjs from 'dayjs'
import ORDER from '@/api/order'
import BID from '@/api/bid'
import { getField, updateField } from 'vuex-map-fields'
import i18n from '@/i18n'
import { MessageBox } from 'element-ui'
import numeral from 'numeral'
import * as currencyConvert from '@/utils/currencyConvert'
import router from '@/router'

import responseMessage from '@/utils/responseMessage'
import ls from '@/utils/ls'

// initial state
const state = {
  // 購物車
  startDate: null,
  endDate: null,
  carts: [],
  addonCarts: [],
  step: 1,
  // Order
  order: {},
  ordererIdentity: '',
  // 填寫資料表單
  form: {
    asOrder: true,
    needSmoke: false,
    specialRequest: false,
    infantBed: false,
    checkInEarly: false,
    checkOutLately: false,
    lowFloor: false,
    noSideRoom: false,
    innerWindow: false,
    bedRequest: null,
    note: '',
    allowances: [],
    customer: {
      firstName: '',
      lastName: '',
      email: '',
      emailConfirm: '',
      phoneCode: '',
      phoneNumber: '',
      gender: 'unknown'
    },
    traveler: {
      firstName: '',
      lastName: '',
      email: '',
      phoneCode: '',
      phoneNumber: '',
      gender: 'unknown'
    },
    sendToTraveler: false,
    needReceipt: false,
    receiptInfo: {
      companyName: '',
      companyNumber: ''
    },
    isOnlyUseTaiwanTravel: false,
    bidNumber: ''
  },
  // 付款相關
  selectedCoupon: {},
  payment: {
    isDiscount: false,
    payMethod: '',
    creditCard: {
      type: '',
      name: '',
      number: '',
      expiryDate: null,
      cvc: ''
    }
  },
  paymentCallback: {},
  isPolicyChecked: false,
  isQuarantineOrder: false,
  taiwanTravel: {
    canUseNum: 0,
    isUsing: false,
    isPolicyChecked: false,
    codeList: []
  },
  trackingLinePoints: false,
  trackingShopback: false,
  firstTrial: true,
  promoCouponCode: null,
  bidder: null,
  mobileCartHeight: 0,
  // 銀行優惠
  bankList: []
}

// getters
const getters = {
  getField,
  order: (state) => {
    return state.order
  },
  currency: (state) => {
    if (state.carts.length > 0 && !_.isEmpty(state.carts[0].hotel.currency)) {
      return state.carts[0].hotel.currency
    }
    return null
  },
  isRuntimeCreditCard: (state) => {
    const runtimeMap = [
      'stripe',
      'paynow-creditcard',
      'paynow-creditcard3ds'
    ]
    return runtimeMap.includes(state.payment.payMethod)
  },
  // carts
  dateRange: (state) => {
    return {
      start: state.startDate,
      end: state.endDate
    }
  },
  inCart: (state) => {
    return _.sortBy(state.carts, 'updateTime')
  },
  addonInCart: (state) => {
    return state.addonCarts
  },
  sortedCart: (state) => {
    return _.sortBy(state.carts, 'id')
  },
  selectedCoupon: (state, getters, rootState, rootGetters) => {
    return state.selectedCoupon
  },
  premiumCoupon: (state) => {
    return state.order?.premiumCoupon
  },
  promoCouponCode: (state) => {
    return state.order?.promoCouponCode
  },
  customer: (state) => {
    return state.form.customer
  },
  traveler: (state) => {
    return state.form.traveler
  },
  trackingLinePoints: (state) => {
    return state.trackingLinePoints
  },
  trackingShopback: (state) => {
    return state.trackingShopback
  },
  bankList: (state) => {
    return state.bankList
  },
  bedTotal: (state, getters, rootState, rootGetters) => {
    const nights = rootGetters['search/totalNights']
    let bedTotal = 0
    for (const item of state.carts) {
      const bedAddon = {
        adult: item.bedAdult,
        child: item.bedChild,
        infant: item.bedInfant
      }
      for (const addon in bedAddon) {
        if (bedAddon[addon] > 0) {
          if (item.hotel.extraBedRules[addon] && item.hotel.extraBedRules[addon].isCharge) {
            bedTotal += bedAddon[addon] * item.hotel.extraBedRules[addon].price * nights
          }
        }
      }
    }
    return bedTotal
  },
  orderTotal: (state, getters, rootState, rootGetters) => {
    const nights = rootGetters['search/totalNights']
    const result = _.sumBy(state.carts, item => {
      return item.count * item.plan.untaxedAvgPrice
    })
    return result * nights + getters.bedTotal
  },
  addonPriceTotal: (state, getters, rootState, rootGetters) => {
    const outPutCurrency = rootGetters['user/selectedCurrency']
    const isInteger = [
      'TWD',
      'JPY'
    ]
    const toInteger = isInteger.includes(outPutCurrency)

    return state.addonCarts.reduce((acc, curr) => {
      let price = null
      if (curr.addon.currency === outPutCurrency) {
        price = Number(curr.addon.price)
      } else {
        const convertedPrice = currencyConvert.convert(curr.addon.price, { inputCurrency: curr.addon.currency, listInteger: true })
        price = numeral(convertedPrice).value()
      }
      if (toInteger) {
        price = numeral(price).format('0')
      }
      acc += Number(price) * curr.count

      return acc
    }, 0)
  },
  orderTotalInteger: (state, getters, rootState, rootGetters) => options => {
    const nights = rootGetters['search/totalNights']

    const isInteger = [
      'TWD',
      'JPY'
    ]
    const isCurrencyConvert = rootGetters['hotels/isCurrencyConvert']
    const config = {
      needConvert: false,
      outPutCurrency: rootGetters['user/selectedCurrency'],
      ...options
    }
    const toInteger = isInteger.includes(config.outPutCurrency)
    const result = _.sumBy(state.carts, item => {
      let price = null
      if (isCurrencyConvert && config.needConvert) {
        const convertedPrice = currencyConvert.convert(item.plan.untaxedAvgPrice, { inputCurrency: item.hotel.currency, listInteger: true })
        price = numeral(convertedPrice).value()
      } else {
        price = item.plan.untaxedAvgPrice
      }
      if (toInteger) {
        price = numeral(price).format('0')
      }
      return Number(price) * item.count
    })

    let bedTotal = null
    if (state.carts.length > 0 && isCurrencyConvert && config.needConvert) {
      const convertedPrice = currencyConvert.convert(getters.bedTotal, { inputCurrency: state.carts[0].hotel.currency, listInteger: true })
      bedTotal = numeral(convertedPrice).value()
    } else {
      bedTotal = getters.bedTotal
    }
    if (toInteger) {
      bedTotal = numeral(bedTotal).format('0')
    }

    let addonTotal = null
    let originAddonTotal = state.addonCarts.reduce((acc, curr) => {
      acc += Number(curr.addon.price) * curr.count
      return acc
    }, 0)

    if (state.addonCarts.length > 0 && isCurrencyConvert && config.needConvert) {
      const convertedPrice = currencyConvert.convert(originAddonTotal, { inputCurrency: state.addonCarts[0].addon.currency, listInteger: true })
      addonTotal = numeral(convertedPrice).value()
    } else {
      addonTotal = originAddonTotal
    }
    if (toInteger) {
      addonTotal = numeral(addonTotal).format('0')
    }

    return result * nights + Number(bedTotal) + Number(addonTotal)
  },
  isDiscount: function (state) {
    return Boolean(state.payment.isDiscount || state.order?.premiumCoupon)
  },
  discountTotal: function (state, getters, rootState, rootGetters) {
    const unit = getters.selectedCoupon ? getters.selectedCoupon.discountUnit : null
    const discount = Number(getters.selectedCoupon.discountValue)
    if (unit === 'price') {
      return discount
    } else if (unit === 'percent') {
      return Math.floor(Number(getters.order.untaxedPrice) * (1 - discount))
    }
    return 0
  },
  taxTotal: function (state, getters, rootState, rootGetters) {
    return getters.roomTotal - getters.unTaxRoomTotal
  },
  unTaxRoomTotal: function (state, getters, rootState, rootGetters) {
    const total = _.sumBy(getters.sortedCart, (item) => {
      return item.plan.untaxedAvgPrice * item.count
    })
    return total * rootGetters['search/totalNights']
  },
  roomTotal: function (state, getters, rootState, rootGetters) {
    const total = _.sumBy(getters.sortedCart, (item) => {
      return item.plan.avgPrice * item.count
    })
    return total * rootGetters['search/totalNights']
  },
  orderTotalPrice: function (state, getters, rootState, rootGetters) {
    let result = null
    if (!_.isEmpty(getters.selectedCoupon) && getters.isDiscount) {
      result = getters.roomTotal - getters.discountTotal
    } else {
      result = getters.roomTotal
    }
    return result
  },
  additional: (state) => {
    let result = []
    const metaList = [
      'needSmoke',
      'infantBed',
      'checkInEarly',
      'checkOutLately',
      'lowFloor',
      'noSideRoom',
      'innerWindow',
      'bedRequest'
    ]
    metaList.forEach(meta => {
      if (meta === 'needSmoke') {
        if (state.form[meta]) {
          result.push(i18n.t(`meta.needSmoke.need`))
        } else {
          result.push(i18n.t(`meta.needSmoke.noNeed`))
        }
      } else if (state.form[meta]) {
        result.push(typeof state.form[meta] === 'boolean' ? i18n.t(`meta.${meta}`) : i18n.t(`meta.${meta}['${state.form[meta]}']`))
      }
    })
    return state.form.specialRequest
      ? result
      : false
  },
  isPolicyChecked: (state) => {
    return state.isPolicyChecked
  },
  isQuarantineOrder: (state) => {
    return state.isQuarantineOrder
  },
  payMethod: (state) => {
    return state.payment.payMethod
  },
  // hasOrderSent: (state) => {
  //   return state.hasOrderSent
  // },
  paymentCallback: (state) => {
    return state.paymentCallback
  },
  ordererIdentity: (state) => {
    return state.ordererIdentity
  },
  isUsingTaiwanTravel: (state) => {
    return state.taiwanTravel.isUsing
  },
  isTravelPolicyChecked: (state) => {
    return state.taiwanTravel.isPolicyChecked
  },
  taiwanTravelCodeList: (state) => {
    return state.taiwanTravel.codeList
  },
  taiwanTravelCanUseNum: (state) => {
    return state.taiwanTravel.canUseNum
  },
  isOnlyUseTaiwanTravel: (state) => {
    return state.form.isOnlyUseTaiwanTravel
  },
  isFirstTrial: (state) => {
    return state.firstTrial
  },
  bidder: (state) => {
    return state.bidder
  },
  step: (state) => {
    return state.step
  },
  mobileCartHeight: (state) => {
    return state.mobileCartHeight
  }
}

// actions
const actions = {
  getBankList ({ commit }) {
    return ORDER.bankSpecialList()
      .then(res => {
        if (res.status === 200) {
          commit('SET_BANK_LIST', res.data)
        }
        return res
      })
  },
  getOrder ({ commit, rootState }) {
    if (!rootState.route.query.orderSerial && rootState.route.query.orderToken) {
      console.error('沒有 orderSerial 和 orederToken')
    }
    const query = {
      orderSerial: rootState.route.query.orderSerial,
      orderToken: rootState.route.query.orderToken
    }
    return ORDER.order(query).then(res => {
      if (res.status === 200) {
        const isQuarantine = !_.isEmpty(res.data.hotel.quarantineHotelNote)
        commit('SET_ORDER', res)
        commit('SET_FIRST_TRIAL', true)
        commit('SET_PROMO_COUPON_CODE', res.data?.promoCouponCode)
        commit('SET_TRAVEL_CAN_USE', res)
        if (res.data.customerAuthType) {
          commit('SET_ORDERER_IDENTITY', res.data.customerAuthType)
        }
        commit('SET_QUARANTINE_ORDER', isQuarantine)
      }
      return res
    })
  },
  setCouponToUpdate: function ({ commit, dispatch, getters, rootState }, payload) {
    return dispatch('orderCoupons', {
      orderSerial: rootState.route.query.orderSerial,
      couponId: getters.isDiscount ? getters.selectedCoupon.id : null
    }).then(res => {
      if (res.status === 200) {
        commit('SET_ORDER', res)
        return res
      } else {
        // 70108 訂單來源為 OwlStay 或 YOKOSO 單，需顯示錯誤訊息且不能選擇Coupon
        // 70004 Coupon無法使用於此飯店
        // 70002 Coupon已使用
        // 70003 Coupon已過期
        // 清除 已選擇 Coupon
        if (res.status === 70114) {
          MessageBox.alert(
            res.msg,
            `${i18n.t('Oops')}!`,
            {
              type: 'error',
              showClose: false,
              confirmButtonText: i18n.t('confirm')
            }).then(() => {
            commit('CLEAR_COUPON')
            commit('RESET_DISCOUNT_SWITCH')
            return Promise.reject(res)
          })
        } else {
          commit('CLEAR_COUPON')
          commit('RESET_DISCOUNT_SWITCH')
          return Promise.reject(res)
        }
      }
    })
  },
  updateAddonCarts: function ({ commit, state }, payload) {
    commit('UPDATE_ADDON_CARTS', payload)
  },
  // onPlanChange
  onPlanChange: function ({ commit, dispatch, state, rootGetters }, payload) {
    const { hotel, room, plan, count } = payload
    const id = `hotel/${hotel.hotelUUID}/room/${room.id}/plan/${plan.id}`
    const index = _.findIndex(state.carts, item => item.id === id)
    commit('user/SET_USER_PATH_HOTEL', hotel.hotelUUID, { root: true })
    // console.debug('onPlanChange', hotel, room, plan, count)
    // console.debug('onPlanChange id/index', id, index)

    if (index < 0 && payload.count > 0) {
      // 建立
      // if (index < 0)
      commit('SET_DATE_RANGE', rootGetters['search/dateRange'])
      commit('CREATE_PLAN', payload)
    }
    if (index > -1) {
      if (count > 0) {
        // 更新內容
        // if (index > 0 && 新 === 0 && 新舊 不存在)
        commit('UPDATE_PLAN', payload)
      }
      if (count === 0) {
        // 刪除
        // if (index > 0 && 新 === 0)
        commit('DELETE_PLAN', payload)
      }
    }
  },
  onSaveDiscount: function ({ commit }, coupon) {
    commit('CLEAR_COUPON')
    commit('SET_SELECTED_COUPON', coupon)
  },
  onDeleteCartItem: function ({ commit }, item) {
    console.debug('onDeleteCartItem', item)
    item = {
      ...item,
      val: 0
    }
    commit('MINUS_PLAN', item)
  },
  orderCoupons: function ({ commit, state, rootState, getters }, { orderSerial, code = null, couponId = null }) {
    if (!code) {
      code = state.firstTrial ? state.promoCouponCode : null
      if (couponId && typeof couponId === 'string') {
        code = state.promoCouponCode
        couponId = null
      }
    }
    return ORDER.orderCoupons({
      orderSerial: rootState.route.query.orderSerial,
      code: code,
      couponId: couponId
    }).then(res => {
      return res
    })
  },
  // 旅館頁 立即訂購
  onSubmitOrder: function ({ commit, state, rootState, getters, rootGetters }, bid) {
    console.debug('onSubmitOrder')

    /* eslint-disable */
    let clientId = null
    try {
      const trackers = ga.getAll()
      var i, len
      for (i = 0, len = trackers.length; i < len; i += 1) {
        if (trackers[i].get('trackingId') === 'UA-40259544-3') {
          clientId = trackers[i].get('clientId')
        }
      }
    } catch (error) {
      console.debug('ga.getAll() event error', error)
    }
    /* eslint-enable */

    let items = []
    for (const item of state.carts) {
      items.push({
        roomId: item.room.id,
        planId: item.plan.id,
        count: item.count,
        bed: {
          adult: item.bedAdult,
          child: item.bedChild,
          infant: item.bedInfant
        },
        ratePlanCode: item.plan.otaPlanId
      })
    }
    console.log('onSubmitOrder', items)
    let data = {
      hotelUUID: state.carts[0].hotel.hotelUUID,
      hotelSource: state.carts[0].hotel.hotelSource,
      duringStartAt: dayjs(rootState.route.query.start).format('YYYY-MM-DD'),
      duringEndAt: dayjs(rootState.route.query.end).format('YYYY-MM-DD'),
      items: [
        ...items
      ],
      hotelUuid: state.carts[0].hotel.hotelUUID,
      clientId: clientId,
      actId: bid ? 'bid2buy' : null,
      addons: state.addonCarts.reduce((acc, curr) => {
        if (curr.count > 0) {
          acc.push({
            id: curr.addon.id,
            count: curr.count
          })
        }
        return acc
      }, [])
    }
    function showResponseMessage (statusCode, res = {}) {
      const messageTitle = statusCode === 11032 ? '提醒您！' : i18n.t('errorOccurred')

      console.error('[訂單建立錯誤]', responseMessage('createOrder', statusCode))
      MessageBox.confirm(
        responseMessage('createOrder', statusCode, res.msg),
        messageTitle,
        {
          type: 'error',
          closeOnClickModal: false,
          closeOnPressEscape: false,
          showCancelButton: false,
          confirmButtonText: i18n.t('confirm')
        }
      )
        .then(() => {
          if (statusCode === 81002 || statusCode === 81001) { // 81001/81002 加購商品相關錯誤
            // 不用errorCode來判斷規則，用回傳的庫存來判斷是更新還是退回上一頁
            if (res.data.addons.length === 0) {
              const ages = rootState.route.query.ages && rootState.route.query.ages.length > 0 ? rootState.route.query.ages.split(',') : []
              const agesResult = _.map(ages, age => {
                return Number(age)
              })
              router.replace({
                name: 'HotelDetail',
                params: { hotelId: rootState.hotels.hotelId },
                query: {
                  source: rootState.hotels.source,
                  start: dayjs(rootState.route.query.start).format('YYYY-MM-DD'),
                  end: dayjs(rootState.route.query.end).format('YYYY-MM-DD'),
                  adult: rootState.route.query.adult || 2,
                  child: rootState.route.query.child || 0,
                  childrenAge: agesResult
                }
              })
            } else {
              commit('hotels/SET_HOTEL_ADDONS', res.data.addons, { root: true })
              commit('CLEAR_ADDON_CARTS')
            }
            return Promise.reject(statusCode)
          }
        })
    }

    // MEMO YOKOSO 跟 其他房源 目前拿人數的方式不同，待改
    if (rootState.hotels.externalHotelSource.includes(data.hotelSource)) {
      data = {
        ...data,
        adult: rootState.yokoso.people.adult,
        child: rootState.yokoso.people.child,
        childrenAge: rootState.yokoso.people.childAge
      }
      return ORDER.createOtaOrder(data).then(res => {
        if (res.status === 200) {
          commit('SET_ORDER', res.data)
          return Promise.resolve(res.data)
        } else {
          showResponseMessage(res.status, res)
          return Promise.reject(res.status)
        }
      })
    } else {
      const ages = rootState.route.query.ages && rootState.route.query.ages.length > 0 ? rootState.route.query.ages.split(',') : []
      const agesResult = _.map(ages, age => {
        return Number(age)
      })
      data = {
        ...data,
        adult: rootState.route.query.adult || 2,
        child: rootState.route.query.child || 0,
        childrenAge: agesResult
      }
      return ORDER.createOrder(data)
        .then(res => {
          if (res.status === 200) {
            commit('SET_ORDER', res.data)
            return Promise.resolve(res.data)
          } else {
            console.log('error', res)
            showResponseMessage(res.status, res)
            return Promise.reject(res.status)
          }
        })
        .catch((err) => {
          return Promise.reject(err)
        })
    }
  },
  onSubmitOrderToBid: function ({ commit, state, rootState, getters, rootGetters }, { bid, order }) {
    console.debug('onSubmitOrderToBid')

    /* eslint-disable */
    let clientId = null
    try {
      const trackers = ga.getAll()
      var i, len
      for (i = 0, len = trackers.length; i < len; i += 1) {
        if (trackers[i].get('trackingId') === 'UA-40259544-3') {
          clientId = trackers[i].get('clientId')
        }
      }
    } catch (error) {
      console.debug('ga.getAll() event error', error)
    }
    /* eslint-enable */
    let items = []
    try {
      for (const room of order.rooms) {
        for (const plan of room.plans) {
          items.push({
            roomId: room.id,
            planId: plan.id,
            count: plan.count,
            bed: plan.bed ? {
              adult: plan.bed.adult,
              child: plan.bed.child,
              infant: plan.bed.infant
            } : null,
            ratePlanCode: plan.otaPlanId
          })
        }
      }
    } catch (e) {
      console.log('Error', e)
    }

    let data = {
      hotelUUID: order.hotel.hotelUUID,
      hotelSource: order.hotel.hotelSource,
      duringStartAt: order.startAt,
      duringEndAt: order.endAt,
      items: [
        ...items
      ],
      hotelUuid: order.hotel.hotelUUID,
      clientId: clientId,
      actId: bid ? 'bid2buy' : null,
      addons: order.extraAddons.reduce((acc, curr) => {
        if (curr.count > 0) {
          acc.push({
            id: curr.id,
            count: curr.count
          })
        }
        return acc
      }, [])
    }
    function showResponseMessage (statusCode) {
      const messageTitle = statusCode === 11032 ? '提醒您！' : i18n.t('errorOccurred')

      console.error('[訂單建立錯誤]', responseMessage('createOrder', statusCode))
      MessageBox.alert(
        responseMessage('createOrder', statusCode),
        messageTitle,
        {
          type: 'error',
          confirmButtonText: i18n.t('confirm')
        }
      )
        .then(() => {
          if (statusCode === 81002 || statusCode === 81001) { // 81001/81002 加購商品相關錯誤
            // 退回上一頁
            router.replace({
              name: 'HotelDetail',
              params: { hotelId: order.hotel.hotelUUID },
              query: {
                source: order.source,
                start: order.startAt,
                end: order.endAt,
                adult: order.adultCount || 2,
                child: order.childCount || 0,
                childrenAge: order.childAges
              }
            })
            return Promise.reject(statusCode)
          }
        })
    }
    // MEMO YOKOSO 跟 其他房源 目前拿人數的方式不同，待改
    if (rootState.hotels.externalHotelSource.includes(data.hotelSource)) {
      data = {
        ...data,
        adult: rootState.yokoso.people.adult,
        child: rootState.yokoso.people.child,
        childrenAge: rootState.yokoso.people.childAge
      }
      return ORDER.createOtaOrder(data).then(res => {
        if (res.status === 200) {
          // commit('SET_ORDER', res.data)
          return Promise.resolve(res.data)
        } else {
          showResponseMessage(res.status)
          return Promise.reject(res)
        }
      })
    } else {
      const ages = order.childAges && order.childAges.length > 0 ? order.childAges : []
      const agesResult = _.map(ages, age => {
        return Number(age)
      })
      data = {
        ...data,
        adult: order.adultCount || 2,
        child: order.childAges.length || 0,
        childrenAge: agesResult
      }
      return ORDER.createOrder(data).then(res => {
        if (res.status === 200) {
          // commit('SET_ORDER', res.data)
          return Promise.resolve(res.data)
        }
        showResponseMessage(res.status)
        return Promise.reject(res)
      })
    }
  },
  onSubmitOrderBid: function ({ commit, state, rootState, getters, rootGetters }) {
    console.debug('onSubmitOrderBid')

    function getGA4TrackingParams () {
      const measurementId = process.env.VUE_APP_GA4_MEASUREMENT_ID
      console.log('measurementId', measurementId)
      if (!measurementId) return {}

      // client_id：從 _ga cookie 取，去掉前綴 GA1.X.
      const clientIdMatch = document.cookie.match(/_ga=([^;]+)/)
      const clientId = clientIdMatch
        ? clientIdMatch[1].split('.').slice(2).join('.')
        : null

      // session_id：從 _ga_{MEASUREMENT_ID} cookie 取，index 2 是 session_id
      const cookieName = `_ga_${measurementId.replace('G-', '')}`
      const sessionIdMatch = document.cookie.match(new RegExp(`${cookieName}=([^;]+)`))
      const sessionIdRaw = sessionIdMatch ? sessionIdMatch[1].split('.')[2] : null
      const sessionId = sessionIdRaw ? sessionIdRaw.match(/^s?(\d+)/)?.[1] ?? null : null

      return {
        ...(clientId ? { ga_client_id: clientId } : { ga_client_id: null }),
        ...(sessionId ? { ga_session_id: sessionId } : { ga_session_id: null })
      }
    }

    const fbpMatch = document.cookie.match(/_fbp=([^;]+)/)
    const fbcMatch = document.cookie.match(/_fbc=([^;]+)/)

    let data = {
      frontHost: process.env.NODE_ENV === 'development' ? 'http://owljourney.owl:9487' : null,
      orderSerial: rootState.route.query.orderSerial,
      bidPrice: state.form.bidNumber,
      currency: rootGetters['user/selectedCurrency'],
      customer: {
        ...state.form.customer,
        phone: state.form.customer.phoneNumber
      },
      meta: {
        note: state.form.note || null
      },
      taxName: state.form.needReceipt ? state.form.receiptInfo.companyName : null,
      taxNumber: state.form.needReceipt ? state.form.receiptInfo.companyNumber : null,
      paymentName: state.payment.payMethod,
      card: {
        cardNumber: state.payment.creditCard.number,
        month: state.payment.creditCard.expiryDate ? state.payment.creditCard.expiryDate.slice(0, 2) : null,
        year: state.payment.creditCard.expiryDate ? state.payment.creditCard.expiryDate.slice(2, 4) : null,
        cvc: state.payment.creditCard.cvc,
        name: state.payment.creditCard.name
      },
      ...getGA4TrackingParams(),
      fbp: fbpMatch ? fbpMatch[1] : null,
      fbc: fbcMatch ? fbcMatch[1] : null,
      allowances: state.form.allowances || null
    }

    // 讀取 line travel token / shopback token
    const linePointsLocalStorage = ls.get('owljourney_cps')
    const shopbackLocalStorage = ls.get('owljourney_shopback')

    if (linePointsLocalStorage && !shopbackLocalStorage) {
      data.owljourney_line_travel_id = linePointsLocalStorage.data
    } else if (!linePointsLocalStorage && shopbackLocalStorage) {
      data.owljourney_shopback_id = shopbackLocalStorage.data
    } else if (linePointsLocalStorage && shopbackLocalStorage) {
      if (shopbackLocalStorage.expiry >= linePointsLocalStorage.expiry) {
        data.owljourney_shopback_id = shopbackLocalStorage.data
      } else {
        data.owljourney_line_travel_id = linePointsLocalStorage.data
      }
    }

    return BID.postBidding(data).then(res => {
      if (res.status === 200) {
        commit('SET_BIDDER_INFO', res.data.bidder)
        commit('SET_ORDER', res.data)
        if (shopbackLocalStorage) {
          ls.remove('owljourney_shopback')
          commit('SET_TRACK_SHOPBACK', false)
        }
        if (linePointsLocalStorage) {
          ls.remove('owljourney_cps')
          commit('SET_TRACK_LINE_POINTS', false)
        }
        return res
      } else {
        if (res.status === 150001 || res.status === 150002 || res.status === 150003) {
          commit('SET_BIDDER_INFO', res.data.bidder)
          return res
        }
        const code = res.status
        let messageTitle = i18n.t('errorOccurred')
        let messageContent = responseMessage('postOrder', code)
        let messageType = 'error'
        let confirmButtonText = i18n.t('confirm')
        let callback = () => {}

        switch (code) {
          default:
            messageTitle = `${i18n.t('Oops')}!`
            messageContent = responseMessage('createOrder', code)
            confirmButtonText = i18n.t('ok')
            callback = () => { location.reload() }
            break
          case 81001:
            messageContent = responseMessage('createOrder', code)
            confirmButtonText = i18n.t('confirm')
            callback = () => {
              router.replace({
                name: 'HotelDetail',
                params: { hotelId: state.order.hotel.hotelUUID },
                query: {
                  source: state.order.source,
                  start: state.order.startAt,
                  end: state.order.endAt,
                  adult: state.order.adultCount || 2,
                  child: state.order.childCount || 0,
                  childrenAge: state.order.childAges
                }
              })
            }
            break
          case 81002:
            messageContent = responseMessage('createOrder', code)
            confirmButtonText = i18n.t('confirm')
            callback = () => {
              router.replace({
                name: 'HotelDetail',
                params: { hotelId: state.order.hotel.hotelUUID },
                query: {
                  source: state.order.source,
                  start: state.order.startAt,
                  end: state.order.endAt,
                  adult: state.order.adultCount || 2,
                  child: state.order.childCount || 0,
                  childrenAge: state.order.childAges
                }
              })
            }
            break
          case 80031:
            // 團購專案不可與其他專案同時訂購，請調整訂單內容
            messageTitle = `${i18n.t('Oops')}!`
            messageContent = res.msg
            confirmButtonText = i18n.t('ok')
            break
          case 100001:
            // 外部房源訂購失敗
            messageTitle = `${i18n.t('Oops')}!`
            messageContent = i18n.t('yokosoResponse.yokoso_order_failed')
            confirmButtonText = i18n.t('ok')
            callback = () => { location.reload() }
            break
          case 100002:
            // 外部房源無回應
            messageTitle = `${i18n.t('Oops')}!`
            messageContent = i18n.t('yokosoResponse.yokoso_order_no_response')
            confirmButtonText = i18n.t('ok')
            callback = () => { location.reload() }
            break
          case 100003:
            // 外部房源付款成功，訂單處理中
            messageTitle = `付款完成`
            messageContent = `訂單處理中`
            messageType = 'success'
            confirmButtonText = i18n.t('ok')
            callback = () => { location.reload() }
        }
        if (shopbackLocalStorage) {
          ls.remove('owljourney_shopback')
          commit('SET_TRACK_SHOPBACK', false)
        }
        if (linePointsLocalStorage) {
          ls.remove('owljourney_cps')
          commit('SET_TRACK_LINE_POINTS', false)
        }
        MessageBox.alert(
          messageContent,
          messageTitle,
          {
            type: messageType,
            showClose: false,
            confirmButtonText
          }).then(() => {
          callback()
        })
        return Promise.reject(res)
      }
    })
  },
  updateOrderInfo: function ({ commit, state, rootState }, params) {
    const orderSerial = rootState.route.query.orderSerial
    const orderToken = rootState.route.query.orderToken

    function getGA4TrackingParams () {
      const measurementId = process.env.VUE_APP_GA4_MEASUREMENT_ID
      console.log('measurementId', measurementId)
      if (!measurementId) return {}

      // client_id：從 _ga cookie 取，去掉前綴 GA1.X.
      const clientIdMatch = document.cookie.match(/_ga=([^;]+)/)
      const clientId = clientIdMatch
        ? clientIdMatch[1].split('.').slice(2).join('.')
        : null

      // session_id：從 _ga_{MEASUREMENT_ID} cookie 取，index 2 是 session_id
      const cookieName = `_ga_${measurementId.replace('G-', '')}`
      const sessionIdMatch = document.cookie.match(new RegExp(`${cookieName}=([^;]+)`))
      const sessionIdRaw = sessionIdMatch ? sessionIdMatch[1].split('.')[2] : null
      const sessionId = sessionIdRaw ? sessionIdRaw.match(/^s?(\d+)/)?.[1] ?? null : null

      return {
        ...(clientId ? { ga_client_id: clientId } : { ga_client_id: null }),
        ...(sessionId ? { ga_session_id: sessionId } : { ga_session_id: null })
      }
    }

    const fbpMatch = document.cookie.match(/_fbp=([^;]+)/)
    const fbcMatch = document.cookie.match(/_fbc=([^;]+)/)

    const data = {
      customer: {
        ...state.form.customer,
        phone: state.form.customer.phoneNumber
      },
      traveler: {
        ...state.form.traveler,
        phone: state.form.traveler.phoneNumber
      },
      sendToTraveler: state.form.sendToTraveler,
      code: state.selectedCoupon.code,
      // couponId: state.selectedCoupon ? state.selectedCoupon.id : null,
      taxName: state.form.needReceipt ? state.form.receiptInfo.companyName : null,
      taxNumber: state.form.needReceipt ? state.form.receiptInfo.companyNumber : null,
      meta: {
        lowFloor: null,
        withoutSide: null,
        windows: null,
        note: null
      },
      allowances: state.form.allowances || null,
      source: state.order.source,
      isOnlyUseTaiwanTravel: state.form.isOnlyUseTaiwanTravel,
      recaptcha_token: params.recaptchaToken || null,
      ...getGA4TrackingParams(),
      fbp: fbpMatch ? fbpMatch[1] : null,
      fbc: fbcMatch ? fbcMatch[1] : null
    }
    if (state.payment.isDiscount) {
      data.couponId = state.selectedCoupon ? state.selectedCoupon.id : null
    } else {
      data.couponId = null
    }
    if (state.isQuarantineOrder) {
      data.meta = {
        lowFloor: null,
        withoutSide: null,
        windows: null,
        note: state.form.note || null
      }
    } else if (state.form.specialRequest) {
      data.meta = {
        lowFloor: state.form.lowFloor || null,
        withoutSide: state.form.noSideRoom || null,
        windows: state.form.innerWindow || null,
        note: state.form.note || null
      }
    }
    console.debug('updateOrderInfo data:', data)
    return ORDER.updateOrderInfo(orderSerial, data).then(res => {
      console.debug('updateOrderInfo', res)
      // "room_stock_not_enough": "Oops! 慢了一步，庫存不足"
      // "order_duplicate_update": "訂單已完成"
      // "order_expired": "訂單已失效，請重新選購"
      if (res.status === 200 && res.data && res.data.orderSerial) {
        commit('SET_ORDER', res)
        return res.data
      } else if (res.status === 81003 || res.status === 81008) {
        // ?? issues #2195
        //  ? 確認訂單狀況
        //  ? 決定是否跳轉頁面
        // 訂單已付過款
        router.replace({
          name: 'OrderResult',
          query: {
            orderSerial: orderSerial,
            orderToken: orderToken
          }
        })
      } else if (res.status === 30009) {
        MessageBox.alert(
          res.msg,
          i18n.t('errorOccurred'),
          {
            type: 'error',
            closeOnClickModal: false,
            closeOnPressEscape: false,
            confirmButtonText: i18n.t('confirm')
          }
        )
      } else if (res.status === 40006 || res.status === 40007) {
        MessageBox.alert(
          res.msg,
          i18n.t('errorOccurred'),
          {
            type: 'error',
            closeOnClickModal: false,
            closeOnPressEscape: false,
            confirmButtonText: i18n.t('confirm')
          }
        )
      } else {
        const code = res.status
        MessageBox.alert(
          responseMessage('createOrder', code),
          i18n.t('errorOccurred'),
          {
            type: 'error',
            closeOnClickModal: false,
            closeOnPressEscape: false,
            confirmButtonText: i18n.t('confirm')
          })
          .then(() => {
            if (code === 81001 || code === 81002) {
              commit('hotels/SET_HOTEL', {}, { root: true })
              commit('hotels/SET_HOTEL_ADDONS', [], { root: true })
              commit('CLEAR_ADDON_CARTS')

              router.replace({
                name: 'HotelDetail',
                params: { hotelId: state.order.hotel.hotelUUID },
                query: {
                  source: state.order.source,
                  start: state.order.startAt,
                  end: state.order.endAt,
                  adult: state.order.adultCount,
                  child: state.order.childCount || 0,
                  ages: state.order.childAges.join(',')
                }
              })
            }
          })
        return res
      }
    }).catch(err => {
      console.error('updateOrderInfo', err)
    })
  },
  orderCheckStatus: function ({ commit }, orderSerial) {
    console.debug('orderCheckStatus', 'orderSerial', orderSerial)
    return ORDER.orderCheckStatus(orderSerial).then(res => {
      if (res.status === 200) {
        console.debug('orderCheckStatus', res)
        return res
      } else {
        MessageBox.alert(res.msg, res.status, {
          type: 'warning',
          confirmButtonText: i18n.t('confirm')
        })
        return res
      }
    })
  },
  orderCancelPreview: function ({ commit, getters, rootState }, params) {
    const { source } = params
    return ORDER.cancelOrderPreview({ orderSerial: rootState.route.query.orderSerial, token: rootState.route.query.orderToken, source: source }).then(res => {
      if (res.status === 200) {
        return res
      } else {
        MessageBox.alert(res.msg, res.status, {
          type: 'warning',
          confirmButtonText: i18n.t('confirm')
        })
      }
    })
  },
  orderCancel: function ({ commit, getters, rootState }, params) {
    const { source } = params
    return ORDER.cancelOrder({ orderSerial: rootState.route.query.orderSerial, token: rootState.route.query.orderToken, source: source }).then(res => {
      if (res.status === 200) {
        return res
      } else {
        MessageBox.alert(res.msg, res.status, {
          type: 'warning',
          confirmButtonText: i18n.t('confirm')
        })
      }
    })
  },
  updateCancelReason: function ({ commit }, data) {
    if (data.reason !== 'other') {
      delete data.reasonSupplement
    }
    return ORDER.updateCancelReason(data)
  },
  getOrderCancelReasons: function ({ commit }) {
    return ORDER.orderCancelReasons()
      .then(res => {
        if (res.status === 200) {
          return res
        }
      })
  },
  getCertificate: function ({ commit }, orderSerial) {
    // MEMO 取得認證 API
    MessageBox.alert(`取得認證功能正在緊急開發中 ${orderSerial}`, '取得認證', {
      confirmButtonText: '好'
    })
  },
  getExperiencesList: function ({ commit }, options = {}) {
    const params = {
      orderSerial: null,
      hotelUUID: null,
      latitude: null,
      longitude: null,
      distance: null,
      ...options
    }
    return ORDER.experiencesList(params).then(res => {
      if (res.status === 200) {
        return res
      }
    })
  },
  checkTaiwanTravelCoupon: function ({ commit, state, rootState }) {
    const orderSerial = rootState.route.query.orderSerial
    const voucherList = JSON.parse(JSON.stringify(state.taiwanTravel.codeList))

    const data = {
      voucherList: voucherList.map(e => {
        delete e.status
        return e
      })
    }

    return ORDER.checkTaiwanTravelCoupon(orderSerial, data).then(res => {
      console.debug('checkTaiwanTravelCoupon', res)
      if (res.status === 200) {
        let codeList = state.taiwanTravel.codeList
        codeList.map((e, index) => {
          e.status = res.data.voucherList[index].status
        })

        commit('SET_TAIWAN_TRAVEL_CODE_LIST', codeList)

        return res
      } else {
        const code = res.status
        if (code !== 10000) {
          MessageBox.alert(
            responseMessage('checkTaiwanTravelCoupon', code),
            i18n.t('errorOccurred'),
            {
              type: 'error',
              confirmButtonText: i18n.t('confirm')
            })
        }
        return res
      }
    }).catch(err => {
      console.error('checkTaiwanTravelCoupon', err)
    })
  },
  getBidding: function ({ commit }, params) {
    return BID.getBidding(params)
      .then(res => {
        if (res.status === 200) {
          commit('SET_BIDDER_INFO', res.data.bidder)
          return res
        }
      })
  },
  postBidding: function ({ commit }, data) {
    return BID.postBidding(data)
      .then(res => {
        if (res.status === 200) {
          return res
        }
      })
  },
  postTask: function ({ commit }, data) {
    return BID.postTask(data)
      .then(res => {
        if (res.status === 200) {
          commit('SET_BIDDER_INFO', res.data.bidder)
          return res
        }
      })
  },
  submitPayment: function ({ commit, state, getters, rootState }, { order, applePayToken = null }) {
    console.debug('--------- submitPayment --------')
    console.debug('order', order)
    console.debug('applePayToken', applePayToken)
    const params = {
      orderSerial: rootState.route.query.orderSerial,
      data: {
        frontHost: process.env.NODE_ENV === 'development' ? 'http://owljourney.owl:9487' : null,
        paymentName: state.payment.payMethod,
        token: applePayToken ? JSON.stringify(applePayToken.paymentData) : null
      }
    }

    if (getters.isRuntimeCreditCard) {
      const card = {
        cardNumber: state.payment.creditCard.number,
        month: state.payment.creditCard.expiryDate ? state.payment.creditCard.expiryDate.slice(0, 2) : null,
        year: state.payment.creditCard.expiryDate ? state.payment.creditCard.expiryDate.slice(2, 4) : null,
        cvc: state.payment.creditCard.cvc,
        name: state.payment.creditCard.name
      }
      params.data = {
        ...params.data,
        card
      }
    }
    // 讀取聯盟網 token
    if (Vue.ls.get('vtm_stat_id')) {
      params.data.owljourney_vtm_stat_id = Vue.ls.get('vtm_stat_id')
    }
    // 讀取 line travel token / shopback token
    const linePointsLocalStorage = ls.get('owljourney_cps')
    const shopbackLocalStorage = ls.get('owljourney_shopback')

    if (linePointsLocalStorage && !shopbackLocalStorage) {
      params.data.owljourney_line_travel_id = linePointsLocalStorage.data
    } else if (!linePointsLocalStorage && shopbackLocalStorage) {
      params.data.owljourney_shopback_id = shopbackLocalStorage.data
    } else if (linePointsLocalStorage && shopbackLocalStorage) {
      if (shopbackLocalStorage.expiry >= linePointsLocalStorage.expiry) {
        params.data.owljourney_shopback_id = shopbackLocalStorage.data
      } else {
        params.data.owljourney_line_travel_id = linePointsLocalStorage.data
      }
    }

    console.debug('params', params)
    console.debug('--------- END of submitPayment --------')

    return ORDER.createPayment(params)
      .then(res => {
        if (res.status === 200) {
          if (Vue.ls.get('vtm_stat_id')) {
            Vue.ls.remove('vtm_stat_id')
          }
          if (linePointsLocalStorage) {
            ls.remove('owljourney_cps')
            commit('SET_TRACK_LINE_POINTS', false)
          }
          if (shopbackLocalStorage) {
            ls.remove('owljourney_shopback')
            commit('SET_TRACK_SHOPBACK', false)
          }
          // GTM GA4 由後端打了
          // window.dataLayer.push({
          //   event: 'purchase',
          //   ecommerce: {
          //     coupon: order.premiumCoupon?.code,
          //     transaction_id: order.orderSerial,
          //     value: order.total,
          //     currency: 'TWD',
          //     items: [{
          //       // item_name: order.rooms[0]?.plans[0]?.name ? `${order.hotel.hotelName}_${order.rooms[0].name}_${order.rooms[0].plans[0].name}` : `${order.hotel.hotelName}_${order.rooms[0].name}`,
          //       item_name: order.hotel.hotelName,
          //       coupon: order.coupon?.code,
          //       quantity: 1,
          //       item_brand: order.from,
          //       price: order.rooms[0]?.plans[0]?.avgPrice,
          //       item_category: 'hotels',
          //       item_id: order.hotel.hotelUUID
          //     }]
          //   }
          // })
          if (window.fbq) {
            window.fbq('track', 'Purchase', {
              value: order.total,
              currency: getters.currency || 'TWD',
              content_ids: [order.hotel.hotelUUID],
              content_name: order.hotel.hotelName,
              content_type: 'hotel',
              num_items: 1
            })
          }
          // commit('RESET_PAYMENT')
          return res
        } else {
          const code = res.status
          let messageTitle = i18n.t('errorOccurred')
          let messageContent = responseMessage('postOrder', code)
          let messageType = 'error'
          let confirmButtonText = i18n.t('confirm')
          let callback = () => {}

          switch (code) {
            case 100001:
              // 外部房源訂購失敗
              if (Vue.ls.get('vtm_stat_id')) {
                Vue.ls.remove('vtm_stat_id')
              }
              if (linePointsLocalStorage) {
                ls.remove('owljourney_cps')
                commit('SET_TRACK_LINE_POINTS', false)
              }
              if (shopbackLocalStorage) {
                ls.remove('owljourney_shopback')
                commit('SET_TRACK_SHOPBACK', false)
              }
              messageTitle = `${i18n.t('Oops')}!`
              messageContent = i18n.t('yokosoResponse.yokoso_order_failed')
              confirmButtonText = i18n.t('ok')

              callback = () => { location.reload() }
              break
            case 100002:
              // 外部房源無回應
              if (Vue.ls.get('vtm_stat_id')) {
                Vue.ls.remove('vtm_stat_id')
              }
              if (linePointsLocalStorage) {
                ls.remove('owljourney_cps')
                commit('SET_TRACK_LINE_POINTS', false)
              }
              if (shopbackLocalStorage) {
                ls.remove('owljourney_shopback')
                commit('SET_TRACK_SHOPBACK', false)
              }
              messageTitle = `${i18n.t('Oops')}!`
              messageContent = i18n.t('yokosoResponse.yokoso_order_no_response')
              confirmButtonText = i18n.t('ok')
              callback = () => { location.reload() }
              break
            case 100003:
              // 外部房源付款成功，訂單處理中
              if (Vue.ls.get('vtm_stat_id')) {
                Vue.ls.remove('vtm_stat_id')
              }
              if (linePointsLocalStorage) {
                ls.remove('owljourney_cps')
                commit('SET_TRACK_LINE_POINTS', false)
              }
              if (shopbackLocalStorage) {
                ls.remove('owljourney_shopback')
                commit('SET_TRACK_SHOPBACK', false)
              }
              messageTitle = `付款完成`
              messageContent = `訂單處理中`
              messageType = 'success'
              confirmButtonText = i18n.t('ok')
              callback = () => { location.reload() }
          }
          MessageBox.alert(
            messageContent,
            messageTitle,
            {
              type: messageType,
              showClose: false,
              confirmButtonText
            }).then(() => {
            callback()
          })
          return Promise.reject(res)
        }
      })
  }
}

// mutations
const mutations = {
  updateField,
  REMOVE_ALLOWANCE: function (state, payload) {
    const index = state.form.allowances.indexOf(payload)
    state.form.allowances.splice(index, 1)
  },
  ADD_ALLOWANCE: function (state, payload) {
    state.form.allowances.push(payload)
  },
  SET_DATE_RANGE: function (state, payload) {
    state.startDate = payload.start
    state.endDate = payload.end
  },
  SET_TRACK_LINE_POINTS (state, payload) {
    state.trackingLinePoints = payload
  },
  SET_TRACK_SHOPBACK (state, payload) {
    state.trackingShopback = payload
  },
  CLEAR_CARTS: function (state, payload) {
    // GTM
    let list = []
    for (const item of state.carts) {
      const { hotel, room, plan } = item
      const id = `hotel/${hotel.hotelUUID}/room/${room.id}/plan/${plan.id}`
      const name = `[${hotel.hotelName}] ${room.name} - ${plan.id}`
      list.push({
        'id': id,
        'sku': id,
        'name': name,
        'category': hotel.hotelName,
        'list': hotel.hotelName,
        'brand': room.name,
        'variant': plan.id,
        'price': plan.avgPrice,
        'quantity': item.count
      })
    }
    window.dataLayer.push({
      'event': 'clearCart',
      'ecommerce': {
        'remove': {
          'products': list
        }
      }
    })

    state.carts = []
  },
  UPDATE_ADDON_CARTS: function (state, payload) {
    const { count, addon } = payload
    const index = _.findIndex(state.addonCarts, item => item.addon.id === addon.id)
    if (index > -1) {
      state.addonCarts[index].count = count
    } else {
      state.addonCarts.push({ addon, count })
    }
  },
  CLEAR_ADDON_CARTS: function (state, payload) {
    state.addonCarts = []
  },
  CLEAR_DATES: function (state, payload) {
    state.startDate = null
    state.endDate = null
  },
  CREATE_PLAN: function (state, payload) {
    const { hotel, room, plan } = payload
    const id = `hotel/${hotel.hotelUUID}/room/${room.id}/plan/${plan.id}`
    const name = `[${hotel.hotelName}] ${room.name} - ${plan.id}`
    state.carts.push({
      id,
      ...payload
    })
    window.fbq('track', 'AddToCart', {
      content_ids: [id],
      content_type: 'hotel',
      content_name: name,
      currency: 'TWD',
      value: plan.avgPrice * payload.count,
      num_items: payload.count
    })
    // GTM
    window.dataLayer.push({
      event: 'addToCart',
      'ecommerce': {
        'add': {
          'products': [
            {
              'id': id,
              'sku': id,
              'name': name,
              'category': hotel.hotelName,
              'list': hotel.hotelName,
              'brand': room.name,
              'variant': plan.id,
              'price': plan.avgPrice,
              'quantity': payload.count
            }
          ]
        }
      }
    })

    // GTM GA4
    window.dataLayer.push({
      event: 'add_to_cart',
      ecommerce: {
        currency: 'TWD',
        value: plan.avgPrice,
        items: [{
          item_category: 'hotels',
          item_category2: hotel.countryTextEn,
          item_brand: 'owljourney',
          item_name: hotel.hotelName,
          price: plan.avgPrice,
          item_id: id,
          quantity: payload.count
        }]
      }
    })
  },
  DELETE_PLAN: function (state, payload) {
    const { hotel, room, plan } = payload
    const id = `hotel/${hotel.hotelUUID}/room/${room.id}/plan/${plan.id}`
    const name = `[${hotel.hotelName}] ${room.name} - ${plan.id}`
    const index = _.findIndex(state.carts, item => item.id === id)
    state.carts.splice(index, 1)
    // GTM
    window.dataLayer.push({
      'event': 'removeFromCart',
      'ecommerce': {
        'remove': {
          'products': [
            {
              'id': id,
              'sku': id,
              'name': name,
              'category': hotel.hotelName,
              'list': hotel.hotelName,
              'brand': room.name,
              'variant': plan.id,
              'price': plan.avgPrice,
              'quantity': payload.count
            }
          ]
        }
      }
    })

    // GTM GA4
    window.dataLayer.push({
      event: 'remove_from_cart',
      ecommerce: {
        currency: 'TWD',
        value: plan.avgPrice,
        items: [{
          item_category: 'hotels',
          item_category2: hotel.countryTextEn,
          item_brand: 'owljourney',
          item_name: name,
          price: plan.avgPrice,
          item_id: id,
          quantity: payload.count
        }]
      }
    })
    console.debug('DELETE_PLAN', payload)
  },
  UPDATE_PLAN: function (state, payload) {
    const { hotel, room, plan } = payload
    const id = `hotel/${hotel.hotelUUID}/room/${room.id}/plan/${plan.id}`
    const name = `[${hotel.hotelName}] ${room.name} - ${plan.id}`
    const index = _.findIndex(state.carts, item => item.id === id)
    console.debug('UPDATE_PLAN', payload)
    const preStateCount = _.cloneDeep(state.carts[index].count)
    state.carts[index].id = id
    state.carts[index].hotel = payload.hotel
    state.carts[index].room = payload.room
    state.carts[index].plan = payload.plan
    state.carts[index].isAddBed = payload.isAddBed
    state.carts[index].bedAdult = payload.bedAdult
    state.carts[index].bedChild = payload.bedChild
    state.carts[index].bedInfant = payload.bedInfant
    state.carts[index].count = payload.count
    if (preStateCount < payload.count) {
      window.fbq('track', 'AddToCart', {
        content_ids: [id],
        content_type: 'hotel',
        content_name: name,
        currency: 'TWD',
        value: plan.avgPrice * payload.count,
        num_items: payload.count
      })
      // GTM
      window.dataLayer.push({
        'event': 'addToCart',
        'ecommerce': {
          'add': {
            'products': [
              {
                'id': id,
                'sku': id,
                'name': name,
                'category': hotel.hotelName,
                'list': hotel.hotelName,
                'brand': room.name,
                'variant': plan.id,
                'price': plan.avgPrice,
                'quantity': payload.count
              }
            ]
          }
        }
      })

      // GTM GA4
      window.dataLayer.push({
        event: 'add_to_cart',
        ecommerce: {
          currency: 'TWD',
          value: plan.avgPrice,
          items: [{
            item_category: 'hotels',
            item_category2: hotel.countryTextEn,
            item_brand: 'owljourney',
            item_name: hotel.hotelName,
            price: plan.avgPrice,
            item_id: id,
            quantity: payload.count
          }]
        }
      })
    } else {
      // GTM
      window.dataLayer.push({
        'event': 'removeFromCart',
        'ecommerce': {
          'remove': {
            'products': [
              {
                'id': id,
                'sku': id,
                'name': name,
                'category': hotel.hotelName,
                'list': hotel.hotelName,
                'brand': room.name,
                'variant': plan.id,
                'price': plan.avgPrice,
                'quantity': payload.count
              }
            ]
          }
        }
      })

      // GTM GA4
      window.dataLayer.push({
        event: 'remove_from_cart',
        ecommerce: {
          currency: 'TWD',
          value: plan.avgPrice,
          items: [{
            item_category: 'hotels',
            item_category2: hotel.countryTextEn,
            item_brand: 'owljourney',
            item_name: name,
            price: plan.avgPrice,
            item_id: id,
            quantity: payload.count
          }]
        }
      })
    }
  },
  CLEAR_COUPON: function (state, payload) {
    state.selectedCoupon = {}
    state.firstTrial = false
  },
  RESET_DISCOUNT_SWITCH: function (state, payload) {
    state.payment.isDiscount = false
  },
  SET_SELECTED_COUPON: function (state, payload) {
    state.selectedCoupon = payload
  },
  CLEAR_CREDIT_CARD: function (state) {
    state.payment.creditCard.type = ''
    state.payment.creditCard.name = ''
    state.payment.creditCard.number = ''
    state.payment.creditCard.expiryDate = null
    state.payment.creditCard.cvc = ''
  },
  SET_ORDER: function (state, payload) {
    state.order = payload.data
  },
  SET_POLICY_CHECKED: function (state, payload) {
    state.isPolicyChecked = payload
  },
  SET_QUARANTINE_ORDER: function (state, payload) {
    state.isQuarantineOrder = payload
  },
  SET_ORDERER_IDENTITY: function (state, payload) {
    state.ordererIdentity = payload
  },
  RESET_PAYMENT: function (state, payload) {
    console.debug('RESET PAYMENT')
    state.payment.isDiscount = false
    state.payment.payMethod = ''
    state.payment.creditCard.type = ''
    state.payment.creditCard.name = ''
    state.payment.creditCard.number = ''
    state.payment.creditCard.expiryDate = null
    state.payment.creditCard.cvc = ''
  },
  SET_TRAVEL_CAN_USE: function (state, payload) {
    state.taiwanTravel.canUseNum = payload.data.canUseTravelTicketCount
  },
  SET_TAIWAN_TRAVEL: function (state, payload) {
    state.taiwanTravel.isUsing = payload
  },
  SET_TRAVEL_POLICY_CHECKED: function (state, payload) {
    state.taiwanTravel.isPolicyChecked = payload
  },
  SET_TAIWAN_TRAVEL_CODE_LIST: function (state, payload) {
    state.taiwanTravel.codeList = payload
  },
  RESET_TAIWAN_TRAVEL_CODE_LIST: function (state, payload) {
    state.taiwanTravel.codeList = []
  },
  SET_TAIWAN_TRAVEL_CARD: function (state, payload) {
    state.form.isOnlyUseTaiwanTravel = payload
  },
  SET_FIRST_TRIAL: function (state, payload) {
    state.firstTrial = payload
  },
  SET_PROMO_COUPON_CODE: function (state, payload) {
    state.promoCouponCode = payload
  },
  SET_BIDDER_INFO: function (state, payload) {
    state.bidder = payload
  },
  SET_STEP: function (state, payload) {
    state.step = payload
  },
  SET_MOBILECART_HEIGHT: function (state, payload) {
    state.mobileCartHeight = payload
  },
  SET_BANK_LIST: function (state, payload) {
    state.bankList = payload
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
